import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import http, { formatError } from "@/lib/api";

export default function LivreOr() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ name: "", location: "", message: "" });
    const [busy, setBusy] = useState(false);

    const load = () => http.get("/testimonials").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Merci de renseigner votre nom et votre message.");
            return;
        }
        setBusy(true);
        try {
            await http.post("/testimonials", form);
            toast.success("Merci ! Votre message sera publié après validation.");
            setForm({ name: "", location: "", message: "" });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main data-testid="livre-or-page" className="pt-32 pb-24 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mb-20 max-w-3xl"
                >
                    <div className="overline mb-4">Livre d'or</div>
                    <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-6">
                        Vos <span className="italic-serif text-gilt">mots</span>,<br />
                        notre encre.
                    </h1>
                    <p className="text-bone-200 text-lg leading-relaxed">
                        Patrick aime savoir ce que ses toiles inspirent. Laissez-lui un mot —
                        il en lit chacun.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-12 gap-12 md:gap-20">
                    {/* Testimonials list */}
                    <div className="md:col-span-7 space-y-12">
                        {items.map((t, i) => (
                            <motion.figure
                                key={t.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: i * 0.05 }}
                                data-testid={`testimonial-${t.id}`}
                                className="border-l border-gilt pl-6 md:pl-8"
                            >
                                <blockquote className="italic-serif text-2xl md:text-3xl leading-snug text-bone-100 mb-4">
                                    « {t.message} »
                                </blockquote>
                                <figcaption className="overline text-bone-300">
                                    {t.name}{t.location ? ` · ${t.location}` : ""}
                                </figcaption>
                            </motion.figure>
                        ))}
                        {items.length === 0 && (
                            <div className="text-bone-400 italic-serif">
                                Soyez le premier à laisser un mot.
                            </div>
                        )}
                    </div>

                    {/* Form */}
                    <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
                        <div className="border border-white/10 p-8 md:p-10 bg-ink-850/50">
                            <div className="overline mb-3">Laisser un mot</div>
                            <h3 className="serif text-3xl mb-8">Une impression à partager ?</h3>
                            <form onSubmit={submit} className="space-y-6">
                                <input
                                    data-testid="testimonial-name"
                                    type="text"
                                    placeholder="Votre nom"
                                    className="input-line"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                />
                                <input
                                    data-testid="testimonial-location"
                                    type="text"
                                    placeholder="Ville (optionnel)"
                                    className="input-line"
                                    value={form.location}
                                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                                />
                                <textarea
                                    data-testid="testimonial-message"
                                    placeholder="Votre message…"
                                    rows={5}
                                    className="input-line resize-none"
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    required
                                />
                                <button data-testid="testimonial-submit" type="submit" className="btn-gilt" disabled={busy}>
                                    {busy ? "Envoi…" : "Envoyer mon mot"}
                                </button>
                                <p className="text-xs text-bone-400">
                                    Votre message sera publié après validation par l'artiste.
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
